// /client/src/components/CreateDeploy.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' }
];

// Helper to format date for datetime-local input
const formatDateTimeLocal = (date) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper to format display date
const formatDisplayDate = (date, timeZone) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleString('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper to check if a scheduled time is in the past
const isScheduledTimeInPast = (scheduledAt) => {
  const scheduled = new Date(scheduledAt);
  if (isNaN(scheduled.getTime())) return true;
  const now = new Date();
  return scheduled.getTime() < now.getTime();
};

// Helper to validate time string format (HH:MM)
const isValidTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return false;
  const match = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

const CreateDeploy = () => {
  const navigate = useNavigate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Step 1: Program Selection
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [deploymentName, setDeploymentName] = useState('');

  // Step 2: Pod Selection
  const [availablePods, setAvailablePods] = useState([]);
  const [selectedPodIds, setSelectedPodIds] = useState([]);

  // Step 3: Schedule
  const [schedule, setSchedule] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [defaultTime, setDefaultTime] = useState('09:00');
  const [pastDateErrors, setPastDateErrors] = useState([]);

  // Derived data
  const selectedPods = availablePods.filter(p => selectedPodIds.includes(p._id));
  const totalContacts = selectedPods.reduce((sum, p) => sum + (p.eligibleContactCount || 0), 0);
  const timeZone = selectedProgram?.timeZone || 'America/Los_Angeles';

  // Get pods that are available (not in another deployment)
  const availablePodsForSelection = useMemo(() => {
    return availablePods.filter(pod => !pod.inDeployment);
  }, [availablePods]);

  // Get pods that are already in a deployment
  const podsInDeployment = useMemo(() => {
    return availablePods.filter(pod => pod.inDeployment);
  }, [availablePods]);

  // Calculate default time as next hour in program's timezone
  useEffect(() => {
    if (selectedProgram?.timeZone) {
      const now = new Date();
      const hourString = now.toLocaleString('en-US', {
        timeZone: selectedProgram.timeZone,
        hour: 'numeric',
        hour12: false
      });
      let nextHour = parseInt(hourString, 10) + 1;
      
      // Wrap around at midnight
      if (nextHour > 23) nextHour = 9;
      
      setDefaultTime(`${String(nextHour).padStart(2, '0')}:00`);
    }
  }, [selectedProgram?.timeZone]);

  // Validate schedule for past dates only
  const validateScheduleForPastDates = useCallback((scheduleToValidate) => {
    const pastErrors = [];
    scheduleToValidate.forEach((item, index) => {
      if (isScheduledTimeInPast(item.scheduledAt)) {
        pastErrors.push(index);
      }
    });
    return pastErrors;
  }, []);

  // Fetch programs with products
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch('/api/deployments/programs-with-products', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch programs');
        
        const data = await response.json();
        setPrograms(data.programs || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  // Fetch active pods when moving to step 2
  useEffect(() => {
    if (currentStep === 2) {
      const fetchPods = async () => {
        try {
          setIsLoading(true);
          const token = localStorage.getItem('token');
          const response = await fetch('/api/deployments/active-pods', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!response.ok) throw new Error('Failed to fetch pods');
          
          const data = await response.json();
          setAvailablePods(data.pods || []);
        } catch (err) {
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchPods();
    }
  }, [currentStep]);

  // Generate schedule when product is selected or dates change
  const generateSchedule = useCallback(() => {
    if (!selectedProgram?.productId || !startDate) return;
    
    if (!isValidTimeString(defaultTime)) {
      console.warn('Invalid time format, skipping schedule generation:', defaultTime);
      return;
    }

    const product = selectedProgram.productId;
    const newSchedule = [];
    const [hours, minutes] = defaultTime.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('Invalid time values:', { hours, minutes });
      return;
    }
    
    const [year, month, day] = startDate.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.warn('Invalid date values:', { year, month, day });
      return;
    }

    for (let i = 0; i < product.days; i++) {
      const msgDate = new Date(year, month - 1, day + i, hours, minutes, 0, 0);
      
      if (isNaN(msgDate.getTime())) {
        console.warn('Invalid date created for day', i + 1);
        continue;
      }
      
      newSchedule.push({
        day: i + 1,
        messageType: 'daily',
        messageIndex: i,
        scheduledAt: msgDate.toISOString(),
        label: `Message ${i + 1}`
      });
    }

    setSchedule(newSchedule);
    
    // Validate for past dates only
    const pastErrors = validateScheduleForPastDates(newSchedule);
    setPastDateErrors(pastErrors);
  }, [selectedProgram, startDate, defaultTime, validateScheduleForPastDates]);

  useEffect(() => {
    if (currentStep === 3 && selectedProgram) {
      generateSchedule();
    }
  }, [currentStep, selectedProgram, startDate, defaultTime, generateSchedule]);

  // Handle schedule item time change
  const handleScheduleTimeChange = (index, newDateTime) => {
    const newDate = new Date(newDateTime);
    
    if (isNaN(newDate.getTime())) {
      console.warn('Invalid datetime value:', newDateTime);
      return;
    }
    
    const updated = [...schedule];
    updated[index].scheduledAt = newDate.toISOString();
    setSchedule(updated);
    
    // Revalidate for past dates only
    const pastErrors = validateScheduleForPastDates(updated);
    setPastDateErrors(pastErrors);
  };

  // Handle default time change
  const handleDefaultTimeChange = (e) => {
    const newTime = e.target.value;
    setDefaultTime(newTime);
  };

  // Step navigation
  const canProceedStep1 = selectedProgram && deploymentName.trim();
  const canProceedStep2 = selectedPodIds.length > 0;
  const canProceedStep3 = schedule.length > 0 && 
    schedule.every(s => s.scheduledAt) && 
    pastDateErrors.length === 0;

  const handleNext = () => {
    setError(null);
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleProgramSelect = (programId) => {
    const program = programs.find(p => p._id === programId);
    setSelectedProgram(program);
  };

  const handlePodToggle = (podId) => {
    const pod = availablePods.find(p => p._id === podId);
    if (pod?.inDeployment) {
      setError(`This pod is already in deployment "${pod.inDeployment.deploymentName}". Each pod can only be used in one deployment at a time.`);
      return;
    }

    setSelectedPodIds(prev => 
      prev.includes(podId) 
        ? prev.filter(id => id !== podId)
        : [...prev, podId]
    );
  };

  const handleSelectAllPods = () => {
    if (selectedPodIds.length === availablePodsForSelection.length) {
      setSelectedPodIds([]);
    } else {
      setSelectedPodIds(availablePodsForSelection.map(p => p._id));
    }
  };

  // Submit deployment
  const handleDeploy = async (saveAsDraft = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Final validation - check for past dates before submitting
      const pastErrors = validateScheduleForPastDates(schedule);
      if (pastErrors.length > 0 && !saveAsDraft) {
        setError(`${pastErrors.length} message(s) are scheduled in the past. Please update the schedule.`);
        setPastDateErrors(pastErrors);
        setCurrentStep(3);
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const deploymentData = {
        name: deploymentName.trim(),
        programId: selectedProgram._id,
        productId: selectedProgram.productId._id,
        pods: selectedPodIds.map(id => ({ podId: id })),
        schedule: schedule.map(s => ({
          day: s.day,
          messageType: s.messageType,
          messageIndex: s.messageIndex,
          scheduledAt: s.scheduledAt
        })),
        timeZone: selectedProgram.timeZone,
        status: saveAsDraft ? 'draft' : 'scheduled'
      };

      const response = await fetch('/api/deployments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deploymentData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to create deployment');
      }

      const data = await response.json();
      
      setShowConfirmModal(false);
      navigate('/deployments', { 
        state: { 
          success: true, 
          message: saveAsDraft 
            ? 'Deployment saved as draft' 
            : 'Deployment scheduled successfully' 
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Program Selection
  const renderStep1 = () => (
    <div className="box">
      <h2 className="title is-4">Step 1: Select Program</h2>
      <p className="subtitle is-6 has-text-grey">
        Choose a program with an attached product to deploy
      </p>

      <div className="field">
        <label className="label">Deployment Name</label>
        <div className="control">
          <input
            type="text"
            className="input"
            placeholder="Enter deployment name"
            value={deploymentName}
            onChange={(e) => setDeploymentName(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label className="label">Program</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select
              value={selectedProgram?._id || ''}
              onChange={(e) => handleProgramSelect(e.target.value)}
            >
              <option value="">-- Select a Program --</option>
              {programs.map(program => (
                <option key={program._id} value={program._id}>
                  {program.programName} ({program.productId?.name})
                </option>
              ))}
            </select>
          </div>
        </div>
        {programs.length === 0 && !isLoading && (
          <p className="help is-warning">
            No programs with linked products found. Create a program with a product first.
          </p>
        )}
      </div>

      {selectedProgram && (
        <div className="notification is-info is-light mt-4">
          <div className="columns">
            <div className="column">
              <strong>Program</strong><br />{selectedProgram.programName}
            </div>
            <div className="column">
              <strong>Product</strong><br />{selectedProgram.productId?.name}
            </div>
            <div className="column">
              <strong>Duration</strong><br />{selectedProgram.productId?.days} messages
            </div>
            <div className="column">
              <strong>Timezone</strong><br />{TIMEZONE_OPTIONS.find(t => t.value === selectedProgram.timeZone)?.label || selectedProgram.timeZone}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Step 2: Pod Selection
  const renderStep2 = () => (
    <div className="box">
      <h2 className="title is-4">Step 2: Select Pods</h2>
      <p className="subtitle is-6 has-text-grey">
        Choose which pods will receive this deployment. Each pod can only be used in one active deployment.
      </p>

      <div className="level mb-4">
        <div className="level-left">
          <div className="level-item">
            <button 
              className="button is-small"
              onClick={handleSelectAllPods}
              disabled={availablePodsForSelection.length === 0}
            >
              {selectedPodIds.length === availablePodsForSelection.length && availablePodsForSelection.length > 0 
                ? 'Deselect All' 
                : `Select All Available (${availablePodsForSelection.length})`}
            </button>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <span className="tag is-info is-medium">
              {selectedPodIds.length} pod{selectedPodIds.length !== 1 ? 's' : ''} selected
            </span>
            <span className="tag is-success is-medium ml-2">
              {totalContacts} total contact{totalContacts !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {podsInDeployment.length > 0 && (
        <div className="notification is-warning is-light mb-4">
          <p>
            <strong>⚠️ {podsInDeployment.length}</strong> pod{podsInDeployment.length !== 1 ? 's are' : ' is'} already in an active deployment and cannot be selected:
          </p>
          <div className="mt-2">
            {podsInDeployment.slice(0, 5).map(pod => (
              <span key={pod._id} className="tag is-warning is-light mr-1 mb-1">
                {pod.podName} → {pod.inDeployment?.deploymentName}
              </span>
            ))}
            {podsInDeployment.length > 5 && (
              <span className="tag is-dark is-light">
                +{podsInDeployment.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {availablePods.length === 0 && !isLoading ? (
        <div className="notification is-warning">
          No active pods available. Create pods with members first.
        </div>
      ) : (
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable">
            <thead>
              <tr>
                <th style={{ width: '50px' }}></th>
                <th>Pod ID</th>
                <th>Pod Name</th>
                <th>Total Members</th>
                <th>Eligible Contacts</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {availablePods.map(pod => {
                const isInDeployment = !!pod.inDeployment;
                const isSelected = selectedPodIds.includes(pod._id);
                
                return (
                  <tr 
                    key={pod._id}
                    className={`
                      ${isSelected ? 'is-selected' : ''}
                      ${isInDeployment ? 'has-background-warning-light' : ''}
                    `}
                    onClick={() => !isInDeployment && handlePodToggle(pod._id)}
                    style={{ 
                      cursor: isInDeployment ? 'not-allowed' : 'pointer',
                      opacity: isInDeployment ? 0.7 : 1
                    }}
                    title={isInDeployment ? `Already in deployment: ${pod.inDeployment.deploymentName}` : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInDeployment}
                        onChange={() => !isInDeployment && handlePodToggle(pod._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td><code>{pod.podId}</code></td>
                    <td>
                      {pod.podName}
                      {isInDeployment && (
                        <span className="ml-2 tag is-warning is-small">
                          In Use
                        </span>
                      )}
                    </td>
                    <td>{pod.totalMemberCount || 0}</td>
                    <td>
                      <span className={`tag ${pod.eligibleContactCount > 0 ? 'is-success' : 'is-warning'}`}>
                        {pod.eligibleContactCount || 0}
                      </span>
                      {pod.eligibleContactCount === 0 && (
                        <span className="has-text-warning ml-2" title="No MMS-verified contacts">
                          ⚠️
                        </span>
                      )}
                    </td>
                    <td>
                      {isInDeployment ? (
                        <span className="tag is-warning is-light is-small" title={`In: ${pod.inDeployment.deploymentName}`}>
                          {pod.inDeployment.deploymentStatus}
                        </span>
                      ) : (
                        <span className="tag is-success is-light is-small">
                          Available
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="colorBlack is-size-7 mx-2 help">
        <span className='is-bold'>Eligible Contacts</span> = Members with MMS opt-in verified. Only these will receive messages.
      </p>
    </div>
  );

  // Step 3: Schedule
  const renderStep3 = () => (
    <div className="box">
      <h2 className="title is-4">Step 3: Schedule Messages</h2>
      <p className="subtitle is-6 has-text-grey">
        Set the delivery date and time for each message.
      </p>

      <div className="columns">
        <div className="column is-one-third">
          <div className="field">
            <label className="label">Start Date</label>
            <div className="control">
              <input
                type="date"
                className="input"
                value={startDate}
                min={(() => {
                  const now = new Date();
                  const year = now.getFullYear();
                  const month = String(now.getMonth() + 1).padStart(2, '0');
                  const day = String(now.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                })()}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="column is-one-third">
          <div className="field">
            <label className="label">Default Time</label>
            <div className="control">
              <input
                type="time"
                className={`input ${!isValidTimeString(defaultTime) && defaultTime !== '' ? 'is-warning' : ''}`}
                value={defaultTime}
                onChange={handleDefaultTimeChange}
              />
            </div>
            {!isValidTimeString(defaultTime) && defaultTime !== '' && (
              <p className="help has-text-warning">(Invalid format)</p>
            )}
          </div>
        </div>
        <div className="column is-one-third">
          <div className="field">
            <label className="label">Timezone</label>
            <div className="control">
              <input
                type="text"
                className="input"
                value={TIMEZONE_OPTIONS.find(t => t.value === timeZone)?.label || timeZone}
                disabled
              />
            </div>
            <p className="mx-3 colorBlack is-size-7 help">Set on the program.</p>
          </div>
        </div>
      </div>

      <button 
        className="button is-info is-small mb-4"
        onClick={generateSchedule}
        disabled={!startDate || !isValidTimeString(defaultTime)}
      >
        Regenerate Schedule
      </button>

      {pastDateErrors.length > 0 && (
        <div className="notification is-danger is-light mb-4">
          <strong>⚠️ Past Schedule Times</strong>
          <p>
            {pastDateErrors.length} message{pastDateErrors.length > 1 ? 's are' : ' is'} scheduled in the past.
            Please update the schedule to use future dates and times before continuing.
          </p>
        </div>
      )}

      {schedule.length > 0 ? (
        <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table className="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>Message</th>
                <th>Type</th>
                <th>Scheduled Date & Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((item, index) => {
                const hasPastError = pastDateErrors.includes(index);
                return (
                  <tr key={index} className={hasPastError ? 'has-background-danger-light' : ''}>
                    <td>
                      <span className='colorBlack'>{item.label}</span>
                    </td>
                    <td>
                      <span className="tag is-light">
                        Message
                      </span>
                    </td>
                    <td>
                      <input
                        type="datetime-local"
                        className={`input is-small ${hasPastError ? 'is-danger' : ''}`}
                        value={formatDateTimeLocal(item.scheduledAt)}
                        min={formatDateTimeLocal(new Date())}
                        onChange={(e) => handleScheduleTimeChange(index, e.target.value)}
                      />
                    </td>
                    <td>
                      {hasPastError ? (
                        <span className="tag is-danger" title="Scheduled in the past">
                          Past
                        </span>
                      ) : (
                        <span className="tag is-success is-light">
                          Valid
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="notification is-warning is-light">
          {!startDate ? (
            'Select a start date to generate the message schedule.'
          ) : !isValidTimeString(defaultTime) ? (
            'Enter a valid time (HH:MM format) to generate the schedule.'
          ) : (
            'Click "Regenerate Schedule" to create the message schedule.'
          )}
        </div>
      )}
    </div>
  );

  // Step 4: Confirmation
  const renderStep4 = () => {
    const totalMessages = schedule.length * totalContacts;
    const firstMessage = schedule[0];
    const lastMessage = schedule[schedule.length - 1];

    return (
      <div className="box">
        <h2 className="title is-4">Step 4: Confirm Deployment</h2>
        <p className="subtitle is-6 has-text-grey">
          Review your deployment settings before deploying.
        </p>

        <div className="content">
          <div className="columns is-multiline">
            <div className="column is-half">
              <div className="box has-background-light">
                <h5 className="title is-6 mb-2">Deployment Info</h5>
                <p><span className='colorBlack is-bold'>Name:</span> {deploymentName}</p>
                <p><span className='colorBlack is-bold'>Program:</span> {selectedProgram?.programName}</p>
                <p><span className='colorBlack is-bold'>Product:</span> {selectedProgram?.productId?.name}</p>
                <p><span className='colorBlack is-bold'>Duration:</span> {selectedProgram?.productId?.days} messages</p>
              </div>
            </div>

            <div className="column is-half">
              <div className="box has-background-light">
                <h5 className="title is-6 mb-2">Recipients</h5>
                <p><span className='colorBlack is-bold'>Pods:</span> {selectedPods.length}</p>
                <p><span className='colorBlack is-bold'>Total Contacts:</span> {totalContacts}</p>
                <p><span className='colorBlack is-bold'>Timezone:</span> {TIMEZONE_OPTIONS.find(t => t.value === timeZone)?.label}</p>
              </div>
            </div>

            <div className="column is-full">
              <div className="box has-background-light">
                <h5 className="title is-6 mb-2">Schedule Summary</h5>
                <div className="columns">
                  <div className="column">
                    <p><span className='colorBlack is-bold'>Total Messages:</span> {schedule.length}</p>
                    <p><span className='colorBlack is-bold'>Total MMS to Send:</span> {totalMessages}</p>
                  </div>
                  <div className="column">
                    <p><span className='colorBlack is-bold'>First Message:</span> {firstMessage && formatDisplayDate(firstMessage.scheduledAt, timeZone)}</p>
                    <p><span className='colorBlack is-bold'>Last Message:</span> {lastMessage && formatDisplayDate(lastMessage.scheduledAt, timeZone)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="column is-full">
              <div className="box has-background-light">
                <h5 className="title is-6 mb-2">Selected Pods</h5>
                <div className="tags">
                  {selectedPods.map(pod => (
                    <span key={pod._id} className="tag is-info is-medium">
                      {pod.podName} ({pod.eligibleContactCount} contacts)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="notification is-warning is-light">
          <strong>Important:</strong> Once scheduled, messages will be queued for delivery at the specified times. 
          Make sure all details are correct before proceeding.
        </div>
      </div>
    );
  };

  // Confirmation Modal
  const renderConfirmModal = () => (
    <div className={`modal ${showConfirmModal ? 'is-active' : ''}`}>
      <div className="modal-background" onClick={() => setShowConfirmModal(false)}></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <h2 className="modal-card-title is-bold">Confirm Deployment</h2>
          <button 
            className="delete" 
            aria-label="close"
            onClick={() => setShowConfirmModal(false)}
          ></button>
        </header>
        <section className="modal-card-body">
          <div className="content">
            <p>You are about to schedule the following deployment:</p>
            <ul>
              <li><span className='is-bold colorBlack'>Name:</span> {deploymentName}</li>
              <li><span className='is-bold colorBlack'>Program:</span> {selectedProgram?.programName}</li>
              <li><span className='is-bold colorBlack'>Pods:</span> {selectedPods.length}</li>
              <li><span className='is-bold colorBlack'>Contacts:</span> {totalContacts}</li>
              <li><span className='is-bold colorBlack'>Messages:</span> {schedule.length}</li>
            </ul>
            <p className="has-text-danger">
              <span className='is-bold colorBlack'>This action will queue {schedule.length * totalContacts} MMS messages for delivery.</span>
            </p>
          </div>
          {error && (
            <div className="notification is-danger is-light">
              {error}
            </div>
          )}
        </section>
        <footer className="modal-card-foot">
          <button 
            className="button is-success mx-1"
            onClick={() => handleDeploy(false)}
            disabled={isLoading}
          >
            {isLoading ? 'Scheduling...' : 'Schedule Deployment'}
          </button>
          <button 
            className="button mx-1"
            onClick={() => setShowConfirmModal(false)}
            disabled={isLoading}
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
          <h1 className="title is-3">Create Deployment</h1>
          
          <div className="box mb-5">
            <div className="columns is-mobile has-text-centered">
              {[
                { num: 1, label: 'Program' },
                { num: 2, label: 'Pods' },
                { num: 3, label: 'Schedule' },
                { num: 4, label: 'Confirm' }
              ].map((step, idx) => (
                <div key={step.num} className="column">
                  <div 
                    className={`
                      is-flex is-justify-content-center is-align-items-center
                      ${currentStep === step.num ? 'has-text-info' : ''}
                      ${currentStep > step.num ? 'has-text-success' : ''}
                    `}
                  >
                    <span 
                      className={`
                        tag is-rounded is-medium
                        ${currentStep === step.num ? 'is-info' : ''}
                        ${currentStep > step.num ? 'is-success' : 'is-light'}
                      `}
                    >
                      {currentStep > step.num ? '' : step.num}
                    </span>
                  </div>
                  <p className={`is-size-7 mt-1 ${currentStep >= step.num ? 'has-text-weight-semibold' : 'has-text-grey'}`}>
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="notification is-danger is-light">
              <button className="delete" onClick={() => setError(null)}></button>
              {error}
            </div>
          )}

          {isLoading && (
            <div className="has-text-centered py-5">
              <span className="icon is-large">
                <i className="fas fa-spinner fa-pulse fa-2x"></i>
              </span>
              <p className="mt-3">Loading...</p>
            </div>
          )}

          {!isLoading && (
            <>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </>
          )}

          {!isLoading && (
            <div className="buttons is-right mt-5">
              <button 
                className="button"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              
              {currentStep > 1 && (
                <button 
                  className="button"
                  onClick={handleBack}
                >
                  Back
                </button>
              )}

              {currentStep < 4 && (
                <button 
                  className="button is-info"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !canProceedStep1) ||
                    (currentStep === 2 && !canProceedStep2) ||
                    (currentStep === 3 && !canProceedStep3)
                  }
                >
                  Next
                </button>
              )}

              {currentStep === 4 && (
                <>
                  <button 
                    className="button is-warning"
                    onClick={() => handleDeploy(true)}
                    disabled={isLoading}
                  >
                    Save as Draft
                  </button>
                  <button 
                    className="button is-success"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={isLoading}
                  >
                    Deploy
                  </button>
                </>
              )}
            </div>
          )}

          {renderConfirmModal()}
          </div>
        </div>
      </section>
    </>
  );
};

export default CreateDeploy;